import { Box, Slider, Typography } from "@mui/material";
import { RtlProvider } from "@mui/system";
import { fmtClock } from "../utils";

// Two controls stacked on one x-axis, both spanning the whole recording so they
// agree with the video's own scrubber:
//   playhead — drag to seek (the only thing that moves the video)
//   trim     — drag the handles to choose what becomes the article
// Above them, a band marking where the chosen show sits in the tape, since the
// timeline is now the full recording rather than just that show.
export default function TrimTimeline({ show, bounds, range, playhead, onSeek, onRangeChange, text }) {
  const span = bounds.max - bounds.min || 1;
  const pct = (v) => `${Math.min(100, Math.max(0, ((v - bounds.min) / span) * 100))}%`;
  const showStart = Math.min(show.startOffsetSec, bounds.max);
  const showEnd = Math.min(show.endOffsetSec, bounds.max);

  // Time runs left-to-right even in Hebrew, because the <video> scrubber sitting
  // directly above is native and always does. MUI's Slider takes its direction
  // from theme context, not the dir attribute, so RtlProvider is the only thing
  // that actually pins it — otherwise the two axes point opposite ways.
  return (
    <RtlProvider value={false}>
    <Box sx={{ direction: "ltr" }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
        <Typography variant="caption" sx={{ color: "text.secondary" }}>
          {text.showWindowLabel}
        </Typography>
        <Typography variant="caption" sx={{ color: "text.secondary" }}>
          {fmtClock(bounds.max)}
        </Typography>
      </Box>

      <Box
        sx={{
          position: "relative",
          height: 6,
          borderRadius: 0.5,
          bgcolor: "action.hover",
          mb: 0.5,
        }}
      >
        <Box
          sx={{
            position: "absolute",
            top: 0,
            bottom: 0,
            left: pct(showStart),
            width: pct(bounds.min + (showEnd - showStart)),
            bgcolor: "action.selected",
            borderRadius: 0.5,
          }}
        />
      </Box>

      <Slider
        aria-label={text.playhead}
        value={Math.min(Math.max(playhead, bounds.min), bounds.max)}
        onChange={(_e, v) => onSeek(v)}
        min={bounds.min}
        max={bounds.max}
        step={1}
        valueLabelDisplay="auto"
        valueLabelFormat={fmtClock}
        size="small"
        sx={{
          // The two sliders must not overlap: MUI's vertical padding is part of
          // the drag target, so an overlap lets one steal the other's drag.
          py: "6px",
          color: "text.primary",
          "& .MuiSlider-rail": { opacity: 0.2 },
          "& .MuiSlider-track": { border: "none", opacity: 0.3 },
          "& .MuiSlider-thumb": {
            width: 4,
            height: 20,
            borderRadius: 0.5,
            "&::before": { boxShadow: "none" },
          },
        }}
      />

      <Slider
        getAriaLabel={(i) => (i === 0 ? text.start : text.end)}
        value={range}
        onChange={onRangeChange}
        min={bounds.min}
        max={bounds.max}
        step={1}
        disableSwap
        valueLabelDisplay="auto"
        valueLabelFormat={fmtClock}
        marks={[
          { value: showStart, label: fmtClock(showStart) },
          { value: showEnd, label: fmtClock(showEnd) },
        ]}
        sx={{
          py: "6px",
          mt: 1.5, // breathing room from the playhead bar above; they are separate controls
          "& .MuiSlider-track": { height: 12, borderRadius: 0.5 },
          "& .MuiSlider-rail": { height: 12, borderRadius: 0.5, opacity: 0.16 },
          "& .MuiSlider-thumb": { width: 12, height: 26, borderRadius: 0.75 },
          "& .MuiSlider-markLabel": { fontSize: 11, opacity: 0.7 },
        }}
      />
    </Box>
    </RtlProvider>
  );
}
